import { useState, useMemo, useCallback, useRef } from 'react';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useDataFetchingClient } from 'data-fetching-client';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { Select } from 'foundations-components/transitional/components/Select';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Link } from 'foundations-components/transitional/components/Link';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { syntheticEvent } from 'foundations-components/transitional/utils/SyntheticEvent';
import { FloatingAlertStore, FloatingAlertList } from 'foundations-components/transitional/components/FloatingAlertList';
import { CACHE_DEBOUNCE_DELAY } from '../../constants/campaignSelect';
import { getHasCampaignsToolAccess } from '../../constants/Permissions';
import { flattenLoadedOptions, formatAssetLabel, sanitizeValue, toCampaignOption } from '../../utils/campaignSelect';
import useLoadCampaignSelectOptions from '../../hooks/useLoadCampaignSelectOptions';
import useLoadInitialCampaignSelectOption from '../../hooks/useLoadInitialCampaignSelectOption';
import { useDebounceCallback } from '../../hooks/useDebounceCallback';
import useHandleCampaignChange from '../hooks/useHandleCampaignChange';
import { useAssetConfiguration } from '../hooks/useAssetConfiguration';
import { hasCampaignsWriteAccess } from '../hooks/useFetchCampaignsAssociationSettings';
import { useFetchAssociatedCampaignsV2, GET_ASSOCIATED_CAMPAIGNS_V2_FIELD_NAME } from '../hooks/useFetchAssociatedCampaignsV2';
import { useFetchAssociatedAssetObjectId } from '../hooks/useFetchAssociatedAssetObjectId';
import { GET_ASSOCIATED_CAMPAIGN_FIELD_NAME } from '../hooks/useFetchAssociatedCampaign';
import MarketingStudioOptinWrapper, { useStudioOptinContext } from '../../components/studio/MarketingStudioOptinWrapper';
import { useEmbeddedEditorContext } from '../../embedded/EmbeddedEditorContext';
import useIsMarketingStudioCanvas from '../../hooks/useIsMarketingStudioCanvas';
import MiniCreateCampaignPanel from '../../components/panels/MiniCreateCampaignPanel';
import CampaignEditPanel from '../../components/panels/CampaignEditPanel';
import CampaignSelectStudioLink from './CampaignSelectStudioLink';
import { CampaignSelectTooltip } from './CampaignSelectTooltip';
import { CampaignsPlatformSelectItem } from './CampaignsPlatformSelectItem';
import { CampaignSelectedValue } from './CampaignSelectedValue';
import CampaignSelectEmptyState from './CampaignSelectEmptyState';
import useTrackCampaignSelectInteraction from '../../hooks/useTrackCampaignSelectInteraction';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const noop = () => {};
const CampaignsPlatformSelect = ({
  objectType,
  assetId,
  crmIdProperty = 'hs_object_id',
  values,
  onChange = () => {},
  save = true,
  FormLabel = undefined,
  disabled = false,
  disabledTooltip,
  formControlStyle,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  editorSelect = true,
  withUIFloatingAlertList = true,
  objectBuilderUiProps
}) => {
  const client = useDataFetchingClient();
  const {
    hasAccessToMarketingStudio
  } = useStudioOptinContext();
  const {
    isInsideMarketingStudio
  } = useEmbeddedEditorContext();
  const isMarketingStudioCanvas = useIsMarketingStudioCanvas();
  const loadedOptionsRef = useRef([]);
  const selectCacheRef = useRef({});
  const [hasOpened, setHasOpened] = useState(false);
  const [showCreatePanel, setShowCreatePanel] = useState(false);
  const [showEmptyState, setShowEmptyState] = useState(false);
  const [createdOptions, setCreatedOptions] = useState([]);
  const [selectedCampaignGuids, setSelectedCampaignGuids] = useState(values || []);
  const hasCampaignsToolAccess = getHasCampaignsToolAccess();
  const {
    associationSettings,
    objectDefinition,
    resolvedObjectType,
    resolvedObjectTypeId,
    isOneToMany,
    loadingAssociationDefinition
  } = useAssetConfiguration(objectType);
  const resolvedIsOneToMany = loadingAssociationDefinition ? true : isOneToMany;
  const hasWriteAccess = Boolean(associationSettings && associationSettings[resolvedObjectType] && hasCampaignsWriteAccess(associationSettings, resolvedObjectType));
  const isSelectDisabled = disabled || !hasWriteAccess;
  const showWritePermissionTooltip = Boolean((associationSettings === null || associationSettings === void 0 ? void 0 : associationSettings[resolvedObjectType]) && !hasWriteAccess);
  const writePermissionTooltip = /*#__PURE__*/_jsx(FormattedMessage, {
    message: "campaignSelect.noPublishCampaignPermission"
  });
  const {
    data: objectId,
    loading: loadingAssociatedAssetObjectId
  } = useFetchAssociatedAssetObjectId({
    objectType,
    assetId,
    crmIdProperty,
    skip: !hasCampaignsToolAccess
  });
  const {
    loading: loadingAssociatedCampaigns
  } = useFetchAssociatedCampaignsV2({
    objectTypeId: resolvedObjectTypeId,
    objectId,
    skip: !hasCampaignsToolAccess || !!values,
    onSuccess: ({
      campaignGuids
    }) => {
      setSelectedCampaignGuids(campaignGuids);
    }
  });
  const {
    initialOptions,
    loading: initialOptionsLoading
  } = useLoadInitialCampaignSelectOption({
    value: resolvedIsOneToMany ? selectedCampaignGuids : selectedCampaignGuids[0],
    multi: resolvedIsOneToMany,
    hasOpened
  });
  const handleOptionsLoaded = useCallback(options => {
    loadedOptionsRef.current = flattenLoadedOptions(options);
  }, []);
  const mergedOptions = useMemo(() => [...initialOptions, ...createdOptions], [initialOptions, createdOptions]);
  const baseLoadOptions = useLoadCampaignSelectOptions({
    multi: resolvedIsOneToMany,
    value: resolvedIsOneToMany ? selectedCampaignGuids : selectedCampaignGuids[0],
    setShowEmptyState,
    setHasLoadedFullOptions: noop,
    onOptionsLoaded: handleOptionsLoaded
  });
  const loadOptions = useCallback((input, callback, pagination) => {
    baseLoadOptions(input, callback, pagination);
  }, [baseLoadOptions]);
  const isLoading = loadingAssociationDefinition || loadingAssociatedAssetObjectId || loadingAssociatedCampaigns || initialOptionsLoading;
  const valueToDisplay = useMemo(() => isLoading ? resolvedIsOneToMany ? [] : undefined : resolvedIsOneToMany ? selectedCampaignGuids : selectedCampaignGuids[0], [isLoading, resolvedIsOneToMany, selectedCampaignGuids]);
  const placeholder = useMemo(() => isLoading || initialOptionsLoading ? /*#__PURE__*/_jsx(LoadingSpinner, {
    grow: true,
    size: "extra-small"
  }) : I18n.text(resolvedIsOneToMany ? 'campaignSelect.multiPlaceholder' : 'campaignSelect.selectCampaign'), [initialOptionsLoading, isLoading, resolvedIsOneToMany]);
  const debouncedCacheEvict = useDebounceCallback(() => {
    client.cache.evict({
      fieldName: GET_ASSOCIATED_CAMPAIGN_FIELD_NAME
    });
  }, CACHE_DEBOUNCE_DELAY);
  const clearCache = useCallback(() => {
    debouncedCacheEvict();
  }, [debouncedCacheEvict]);
  const handleChangeSuccess = useCallback(params => {
    var _params$updatedCampai, _ref, _params$selectedOptio, _params$selectedOptio2, _loadedOptionsRef$cur;
    client.cache.evict({
      fieldName: GET_ASSOCIATED_CAMPAIGN_FIELD_NAME
    });
    client.cache.evict({
      fieldName: GET_ASSOCIATED_CAMPAIGNS_V2_FIELD_NAME
    });
    const asset = objectDefinition === null || objectDefinition === void 0 ? void 0 : objectDefinition.singularForm;
    const updatedGuids = (_params$updatedCampai = params === null || params === void 0 ? void 0 : params.updatedCampaignGuids) !== null && _params$updatedCampai !== void 0 ? _params$updatedCampai : [];
    const isRemoval = updatedGuids.length < selectedCampaignGuids.length || !updatedGuids[0];
    const changedGuid = isRemoval ? selectedCampaignGuids.find(guid => !updatedGuids.includes(guid)) : updatedGuids.find(guid => !selectedCampaignGuids.includes(guid));
    const campaignName = (_ref = (_params$selectedOptio = params === null || params === void 0 || (_params$selectedOptio2 = params.selectedOption) === null || _params$selectedOptio2 === void 0 ? void 0 : _params$selectedOptio2.text) !== null && _params$selectedOptio !== void 0 ? _params$selectedOptio : (_loadedOptionsRef$cur = loadedOptionsRef.current.find(c => c.guid === changedGuid)) === null || _loadedOptionsRef$cur === void 0 ? void 0 : _loadedOptionsRef$cur.text) !== null && _ref !== void 0 ? _ref : '';
    const additionalCampaigns = Math.max(updatedGuids.length - 1, 0);
    const titleKey = isRemoval ? 'campaignSelect.success.platformRemoveTitle' : 'campaignSelect.success.platformAddTitle';
    const messageKey = isRemoval ? null : 'campaignSelect.success.platformAddMessage';
    FloatingAlertStore.addAlert({
      message: messageKey ? I18n.text(messageKey, {
        asset
      }) : undefined,
      titleText: I18n.text(titleKey, {
        asset,
        campaignName,
        additionalCampaigns
      }),
      type: 'info'
    });
  }, [client, objectDefinition === null || objectDefinition === void 0 ? void 0 : objectDefinition.singularForm, selectedCampaignGuids]);
  const handleChangeFailure = useCallback(() => {
    FloatingAlertStore.addAlert({
      message: I18n.text('campaignSelect.failure.platformMessage', {
        asset: formatAssetLabel(objectType, objectDefinition === null || objectDefinition === void 0 ? void 0 : objectDefinition.singularForm)
      }),
      titleText: I18n.text('campaignSelect.failure.platformTitle'),
      type: 'danger'
    });
  }, [objectType, objectDefinition === null || objectDefinition === void 0 ? void 0 : objectDefinition.singularForm]);
  const handleCampaignChange = useHandleCampaignChange({
    objectType,
    assetObjectTypeId: resolvedObjectTypeId,
    onChange,
    save,
    isOneToMany: resolvedIsOneToMany,
    selectedCampaignGuids,
    selectedAssetCrmId: objectId,
    setSelectedCampaignGuids,
    onSuccess: handleChangeSuccess,
    onFailure: handleChangeFailure
  });
  const handleSelectedOptionChange = useCallback(({
    target: {
      value: optionOrOptions
    }
  }) => {
    if (isSelectDisabled) {
      return;
    }
    if (resolvedIsOneToMany) {
      const options = Array.isArray(optionOrOptions) ? optionOrOptions : optionOrOptions ? [optionOrOptions] : [];
      const updatedGuids = options.map(o => sanitizeValue(o.value));
      handleCampaignChange(syntheticEvent(updatedGuids), options, undefined);
    } else {
      const option = Array.isArray(optionOrOptions) ? optionOrOptions[0] : optionOrOptions;
      const guid = option ? sanitizeValue(option.value) : '';
      const isDeselect = !guid;
      handleCampaignChange(syntheticEvent(isDeselect ? '' : guid), isDeselect ? [] : [option], isDeselect ? undefined : option);
    }
  }, [isSelectDisabled, resolvedIsOneToMany, handleCampaignChange]);
  const {
    handleSelectedOptionChangeWithTracking,
    trackCampaignSelection
  } = useTrackCampaignSelectInteraction({
    enableTracking: true,
    handleCampaignAutoSelectChange: noop,
    handleSelectedOptionChange,
    assetObjectType: objectType
  });
  const handleToggle = useCallback(option => {
    if (isSelectDisabled) {
      return;
    }
    const sanitizedGuid = sanitizeValue(option.value);
    const isRemoving = selectedCampaignGuids.includes(sanitizedGuid);
    if (!isRemoving) {
      var _option$containsSearc, _option$isRecentlyUse;
      trackCampaignSelection({
        action: 'select a campaign',
        containsSearchQuery: (_option$containsSearc = option.containsSearchQuery) !== null && _option$containsSearc !== void 0 ? _option$containsSearc : undefined,
        isRecentlyUsed: (_option$isRecentlyUse = option.isRecentlyUsed) !== null && _option$isRecentlyUse !== void 0 ? _option$isRecentlyUse : undefined,
        campaignId: option.guid,
        assetId: option.objectId,
        assetType: objectType
      });
    }
    const updatedGuids = isRemoving ? selectedCampaignGuids.filter(guid => guid !== sanitizedGuid) : [...selectedCampaignGuids, sanitizedGuid];
    const selectOptions = updatedGuids.map(guid => loadedOptionsRef.current.find(o => o.guid === guid)).filter(Boolean);
    handleCampaignChange(syntheticEvent(updatedGuids), selectOptions, isRemoving ? undefined : option);
  }, [isSelectDisabled, selectedCampaignGuids, handleCampaignChange, objectType, trackCampaignSelection]);
  const onCampaignCreated = useCallback(campaign => {
    if (isSelectDisabled) {
      return;
    }
    const option = toCampaignOption(campaign);
    setCreatedOptions(prev => [...prev, option]);
    if (resolvedIsOneToMany) {
      const updatedGuids = [...selectedCampaignGuids, option.guid];
      const selectOptions = [...loadedOptionsRef.current.filter(o => selectedCampaignGuids.includes(o.guid)), option];
      handleCampaignChange(syntheticEvent(updatedGuids), selectOptions, option);
    } else {
      handleCampaignChange(syntheticEvent(option.guid), [option], option);
    }
    selectCacheRef.current = {};
    setShowEmptyState(false);
  }, [isSelectDisabled, resolvedIsOneToMany, selectedCampaignGuids, handleCampaignChange]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [withUIFloatingAlertList && /*#__PURE__*/_jsx(FloatingAlertList, {
      "data-test-id": "campaigns-lib-floating-alert-list"
    }), /*#__PURE__*/_jsx(FormControl, Object.assign({
      label: I18n.text(resolvedIsOneToMany ? 'campaignSelect.multiMarketingCampaign' : 'campaignSelect.marketingCampaign'),
      FormLabel: FormLabel,
      tooltip: disabledTooltip || /*#__PURE__*/_jsx(CampaignSelectTooltip, {}),
      tooltipPlacement: "right",
      onBlur: clearCache
    }, formControlStyle, {
      children: /*#__PURE__*/_jsx("span", {
        "data-test-id": "campaign-select-component",
        children: showEmptyState && !isInsideMarketingStudio ? /*#__PURE__*/_jsx(CampaignSelectEmptyState, {
          objectType: objectType,
          disabled: isSelectDisabled,
          disabledTooltip: showWritePermissionTooltip ? writePermissionTooltip : undefined,
          onClickCreate: () => {
            if (isSelectDisabled) {
              return;
            }
            setShowCreatePanel(true);
            setHasOpened(true);
          }
        }) : /*#__PURE__*/_jsx(Tooltip, {
          disabled: !showWritePermissionTooltip,
          title: writePermissionTooltip,
          children: /*#__PURE__*/_jsx("span", {
            children: /*#__PURE__*/_jsx(Select, {
              "data-test-id": "campaigns-platform-select",
              value: valueToDisplay,
              options: mergedOptions,
              loadOptions: loadOptions
              // eslint-disable-next-line react-hooks/refs
              ,
              cache: selectCacheRef.current,
              autoload: true,
              searchable: true,
              minimumSearchCount: 1,
              onOpenChange: () => {
                if (!isSelectDisabled && !hasOpened) {
                  setHasOpened(true);
                }
              },
              multi: resolvedIsOneToMany,
              multiCollapseLimit: 1,
              anchorType: resolvedIsOneToMany ? 'button' : 'auto',
              optionRenderer: option => {
                var _option$value;
                return /*#__PURE__*/_jsx(CampaignsPlatformSelectItem, {
                  option: option,
                  isOneToMany: resolvedIsOneToMany,
                  isSelected: selectedCampaignGuids.includes(sanitizeValue((_option$value = option.value) !== null && _option$value !== void 0 ? _option$value : '')),
                  onToggle: () => handleToggle(option)
                });
              },
              optionRendererIgnoresSpecialOptions: true,
              onSelectedOptionChange: handleSelectedOptionChangeWithTracking,
              valueComponent: CampaignSelectedValue,
              placeholder: placeholder,
              clearable: !resolvedIsOneToMany,
              disabled: isSelectDisabled,
              dropdownFooter: /*#__PURE__*/_jsx(Link, {
                disabled: isSelectDisabled,
                onClick: () => {
                  if (!isSelectDisabled) {
                    setShowCreatePanel(true);
                  }
                },
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "campaignSelect.createCampaign"
                })
              })
            })
          })
        })
      })
    })), hasAccessToMarketingStudio && !isInsideMarketingStudio && !isMarketingStudioCanvas && selectedCampaignGuids.length === 1 && /*#__PURE__*/_jsx(CampaignSelectStudioLink, {
      campaignGuid: selectedCampaignGuids[0]
    }), showCreatePanel && /*#__PURE__*/_jsx(_Fragment, {
      children: hasAccessToMarketingStudio && !isInsideMarketingStudio ? /*#__PURE__*/_jsx(MiniCreateCampaignPanel, {
        onClose: () => setShowCreatePanel(false),
        onCreated: onCampaignCreated,
        assetObjectType: objectType
      }) : /*#__PURE__*/_jsx(CampaignEditPanel, {
        appName: "",
        onClose: response => {
          if (response && response.createdObject) {
            onCampaignCreated(response.createdObject);
          }
          setShowCreatePanel(false);
        },
        use: "create",
        objectBuilderUiProps: objectBuilderUiProps,
        assetObjectType: objectType
      })
    })]
  });
};
const CampaignsPlatformSelectWithOptinWrapper = props => {
  return /*#__PURE__*/_jsx(MarketingStudioOptinWrapper, {
    shouldRenderOptin: false,
    children: /*#__PURE__*/_jsx(CampaignsPlatformSelect, Object.assign({}, props))
  });
};
export default CampaignsPlatformSelectWithOptinWrapper;